import { sessionWindowsAtomFamily, type Tab } from '@/atoms/sessionWindowsAtoms';
import type { UiOpenArtifactWire } from '@heidi/agents/contracts';
import { TabType } from '@heidi/session/contracts';
import { produce } from 'immer';
import { atom, getDefaultStore } from 'jotai';

/**
 * Pending tab focus for when SplitScreen hasn't seeded `sessionWindows` yet
 * (e.g. navigate to another session, then focus a document after mount).
 */
export type VoiceSessionFocusRequest = {
  sessionId: string;
  tab: Tab;
  requestedAt: number;
};

export const voiceSessionFocusAtom = atom<VoiceSessionFocusRequest | null>(null);

/** Map a classic-Scribe ``ui.open_artifact`` payload to a session tab, if any. */
export const tabFromOpenArtifact = (data: UiOpenArtifactWire): Tab | null => {
  switch (data.type) {
    case 'note':
      return { name: TabType.NOTE };
    case 'document': {
      const documentId = data.document_id?.trim();
      if (!documentId) return null;
      return { name: TabType.DOCUMENT, documentId };
    }
    case 'session': {
      const active = data.active_tab?.trim();
      const tabType = Object.values(TabType).find((value) => value === active);
      if (tabType) {
        if (tabType === TabType.DOCUMENT) {
          const documentId = data.document_id?.trim();
          if (!documentId) return null;
          return { name: TabType.DOCUMENT, documentId };
        }
        return { name: tabType };
      }
      return null;
    }
    default:
      return null;
  }
};

/**
 * Focus a tab on the session windows atom (same as ``addTabAndSetActive``).
 * Returns false when the window isn't seeded yet — callers should keep a
 * pending request so a mount hook can retry.
 */
export const applySessionTabFocus = (sessionId: string, tab: Tab): boolean => {
  const store = getDefaultStore();
  const windowsAtom = sessionWindowsAtomFamily(sessionId);
  const data = store.get(windowsAtom);
  if (!data?.sessionWindows?.length) {
    return false;
  }
  const windowIndex = data.activeWindowIndex ?? 0;
  if (!data.sessionWindows.at(windowIndex)) {
    return false;
  }

  store.set(windowsAtom, (windowsData) =>
    produce(windowsData, (draft) => {
      const sessionWindow = draft.sessionWindows.at(windowIndex);
      if (!sessionWindow) return;
      const tabExists = sessionWindow.tabs.some(
        (t) => t.name === tab.name && t.documentId === tab.documentId
      );
      if (!tabExists) {
        sessionWindow.tabs.push(tab);
      }
      sessionWindow.activeTab = tab;
    })
  );
  return true;
};

/**
 * Request focus now; if windows aren't ready, stash for the session-page hook.
 */
export const requestSessionTabFocus = (sessionId: string, tab: Tab): void => {
  const store = getDefaultStore();
  store.set(voiceSessionFocusAtom, {
    sessionId,
    tab,
    requestedAt: Date.now(),
  });
  if (applySessionTabFocus(sessionId, tab)) {
    store.set(voiceSessionFocusAtom, null);
  }
};
